import createClient from 'openapi-fetch'
import type { paths } from './generated/schema'
import { createAuthMiddleware } from './authMiddleware'
import { createDeviceMiddleware } from './deviceMiddleware'
import { getDeviceId } from '../auth/deviceId'
import { createMaintenanceMiddleware } from './maintenanceMiddleware'
import { setMaintenance } from './maintenanceStore'
import { createSessionRevokedMiddleware } from './sessionRevokedMiddleware'
import { reportSessionRevoked } from './sessionRevokedStore'
import { withTimeout } from './requestTimeout'
import { resolveApiBase } from '../config/backendOverride'
import { env } from '../config/env'

/**
 * The typed API client. One instance, one place auth is attached.
 *
 * No default base URL. The plan specified
 * `import.meta.env.VITE_API_BASE ?? 'https://stag-api.tracke5.com'`, but a
 * fallback to a real tier is worse than no client at all: under vitest the var
 * is undefined, so every unmocked request in the suite would reach the live
 * staging backend, and a deploy that lost the variable would look healthy
 * while talking to the wrong environment. `tracke5:require-env` in
 * vite.config.ts fails the build when it is missing; `config/env.ts`'s
 * `required()` is the runtime backstop for the case the gate cannot see.
 *
 * The base comes from `config/env.ts`, the one place VITE_API_BASE is read (#173).
 * Its `required()` throws the actionable message — which `.env.<mode>` to edit,
 * and that tests stub it — and `client.test.ts` pins that it names the variable.
 */
/**
 * The base every request uses: the build's own, unless a developer chose
 * another backend in Settings › Developer (#449).
 *
 * **The condition is inline, and it is the production gate.** Vite folds
 * `import.meta.env.MODE` to a literal, so in a production build this is
 * `"production" !== "production" ? … : env.apiBase` — the override branch is dead
 * and `config/backendOverride` (its host list, its storage key) is never
 * emitted. `backendOverride.build.test.ts` checks the artifact rather than
 * trusting this sentence. MODE rather than `DEV`, because `build:dev` and
 * `build:stag` are `DEV === false` builds that keep it.
 *
 * Resolved once, here, because `api` below captures it at construction.
 * Switching backends therefore reloads the page.
 */
export const API_BASE =
  import.meta.env.MODE !== 'production' ? resolveApiBase(env.apiBase) : env.apiBase

export const api = createClient<paths>({ baseUrl: API_BASE, fetch: withTimeout() })

/**
 * Resolved per request rather than held as a token, so a refresh underneath us
 * is picked up on the next call. Phase 2 (auth) installs the Firebase provider.
 */
let tokenProvider: () => Promise<string | null> = async () => null

export function setTokenProvider(fn: () => Promise<string | null>) {
  tokenProvider = fn
}

// The indirection is load-bearing: `createAuthMiddleware(tokenProvider)` would
// capture the initial no-op provider and never see a later one.
api.use(createAuthMiddleware(() => tokenProvider()))

// X-Device-Id on every request, not only the session one — the backend's revoke
// rollout requires it. `deviceId.ts` imports nothing, so api → auth has no cycle.
api.use(createDeviceMiddleware(getDeviceId))

// The third middleware (auth, device, maintenance) — #274. `setMaintenance` is the module-scope store's
// own writer, so this line is the only place client.ts and the maintenance
// system touch: everything about recognising and validating a 503 body lives
// in maintenanceMiddleware.ts, everything about holding and broadcasting the
// result lives in maintenanceStore.ts.
api.use(createMaintenanceMiddleware(setMaintenance))

// A 401 whose body says the session, or this device, was revoked on the server. The detector
// only reports; `AuthProvider` subscribes to the store and signs out (single-flight lives in
// the store). Every other 401 is untouched.
api.use(createSessionRevokedMiddleware(reportSessionRevoked))
